import { useState } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import { useFormik } from 'formik'
import { Loader, StickyActionBar } from '~/shared/ui'
import {
  refusalDetail,
  useDiscardPolicyConfiguration,
  usePolicyConfiguration,
  usePublishPolicyConfiguration,
  useSavePolicyConfigurationSections,
  type PolicyConfigurationDetail,
} from './BenefitPolicyService'
import { AccumulatorsSection } from './AccumulatorsSection'
import { BenefitInformationSection } from './BenefitInformationSection'
import { CoverageDatesSection } from './CoverageDatesSection'
import { DiscardDraftDialog } from './DiscardDraftDialog'
import { DocumentsSection } from './DocumentsSection'
import { PharmacySection } from './PharmacySection'
import { PointSolutionsSection } from './PointSolutionsSection'
import { PolicyStatusPill } from './PolicyStatusPill'
import { ReviewPublishDialog } from './ReviewPublishDialog'
import { SavePublishedDialog } from './SavePublishedDialog'
import {
  editorValidationSchema,
  editorValuesFrom,
  sectionsFrom,
  type PolicyEditorValues,
} from './policyEditorForm'

interface BenefitPolicyEditorProps {
  policyId: number
  onBack: () => void
}

/**
 * What the sticky bar reports.
 *
 * A refusal from the last save comes first: it is the answer to what the operator just did, and it
 * names something the publish checklist cannot — a plan id another configuration already holds, or
 * a period that has since ended. The outstanding items are still there once it clears.
 */
const IssueSummary = ({
  policy,
  refusal,
}: {
  policy: PolicyConfigurationDetail
  refusal: string | undefined
}) => {
  if (refusal) {
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }} data-testid="editor-refusal">
        {refusal}
      </Typography>
    )
  }
  const [first, ...rest] = policy.validation_issues
  if (!first) {
    return (
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <CheckCircleOutlineIcon fontSize="small" color="success" />
        <Typography variant="body2" data-testid="editor-issues">
          All validations pass.
        </Typography>
      </Stack>
    )
  }
  return (
    <Typography variant="body2" sx={{ color: 'warning.main' }} data-testid="editor-issues">
      {first.message}
      {rest.length > 0 && ` (+${rest.length} more)`}
    </Typography>
  )
}

const EditorForm = ({
  policy,
  onBack,
}: {
  policy: PolicyConfigurationDetail
  onBack: () => void
}) => {
  const saveSections = useSavePolicyConfigurationSections(policy.id)
  const publishPolicy = usePublishPolicyConfiguration(policy.id)
  const discardPolicy = useDiscardPolicyConfiguration(policy.id)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false)

  // A published policy whose period has run out is a historical record — the server refuses writes
  // to it, so the editor says so rather than letting the operator type into a form that cannot save.
  const hasEnded = policy.status === 'published' && policy.display_state === 'expired'
  const readOnly = !policy.can_edit || hasEnded

  // Documents and benefit information commit on their own, so on the policy members are reading
  // today they land immediately — no publish step stands between the operator and the member.
  // The active state rather than the published one: an upcoming plan year is published, not in force.
  const isActive = policy.display_state === 'active'

  // Recomputed every render from the cached policy, which the save writes back to — so the form
  // always reinitializes onto what the server last stored.
  const storedValues = editorValuesFrom(policy)

  const sectionsBody = (values: PolicyEditorValues) =>
    sectionsFrom(values, {
      individual: policy.individual_accumulator !== null,
      family: policy.family_accumulator !== null,
    })
  const save = (values: PolicyEditorValues) => saveSections.mutateAsync(sectionsBody(values))

  const formik = useFormik<PolicyEditorValues>({
    // Reinitializing on the save response is what resets `dirty`, so the bar settles once the
    // server has confirmed what it stored.
    enableReinitialize: true,
    initialValues: storedValues,
    validationSchema: editorValidationSchema,
    onSubmit: async values => {
      if (policy.status !== 'draft') {
        saveSections.reset()
        setConfirmSaveOpen(true)
        return
      }
      await save(values)
    },
  })

  const isDraft = policy.status === 'draft'

  const refusal = saveSections.isError
    ? refusalDetail(saveSections.error, 'Could not save this policy.')
    : undefined
  const publishRefusal = publishPolicy.isError
    ? refusalDetail(publishPolicy.error, 'Could not publish this policy.')
    : undefined
  const discardRefusal = discardPolicy.isError
    ? refusalDetail(discardPolicy.error, 'Could not discard this draft.')
    : undefined

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ ml: -1, mb: 0.5 }}
            data-testid="editor-back"
          >
            All policies
          </Button>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {policy.contract_name ?? 'Policy'}
            </Typography>
            <PolicyStatusPill displayState={policy.display_state} testId="editor-status" />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {policy.contracted_entity_label}
          </Typography>
        </Box>
      </Stack>

      {!policy.can_edit && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="editor-read-only-banner">
          You have read-only access. Editing is limited to the Implementation Ops group.
        </Alert>
      )}
      {hasEnded && (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="editor-ended-banner">
          This policy has ended and is read-only. Create a new configuration for the next plan year.
        </Alert>
      )}
      {policy.warnings.map(warning => (
        <Alert key={warning} severity="warning" sx={{ mt: 2 }} data-testid="editor-warning">
          {warning}
        </Alert>
      ))}

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <CoverageDatesSection formik={formik} policy={policy} readOnly={readOnly} />
          <AccumulatorsSection formik={formik} readOnly={readOnly} />
          <PharmacySection formik={formik} readOnly={readOnly} />
          <DocumentsSection
            policyId={policy.id}
            documents={policy.documents}
            isActive={isActive}
            readOnly={readOnly}
          />
          <BenefitInformationSection
            policyId={policy.id}
            benefitInformation={policy.benefit_information}
            documents={policy.documents}
            isActive={isActive}
            readOnly={readOnly}
          />
          <PointSolutionsSection
            policyId={policy.id}
            pointSolutions={policy.point_solutions}
            isActive={isActive}
            readOnly={readOnly}
          />
        </Stack>

        {!readOnly && (
          <StickyActionBar
            testId="editor-action-bar"
            status={<IssueSummary policy={policy} refusal={refusal} />}
          >
            {isDraft && (
              <Button
                color="error"
                onClick={() => {
                  discardPolicy.reset()
                  setDiscardOpen(true)
                }}
                data-testid="editor-discard"
              >
                Discard
              </Button>
            )}
            <Button
              type="submit"
              variant={isDraft ? 'outlined' : 'contained'}
              disabled={!formik.dirty || formik.isSubmitting}
              data-testid="editor-save"
            >
              {isDraft ? 'Save draft' : 'Save changes'}
            </Button>
            {isDraft && (
              <Button
                variant="contained"
                onClick={() => {
                  publishPolicy.reset()
                  setReviewOpen(true)
                }}
                disabled={policy.validation_issues.length > 0 || formik.dirty}
                data-testid="editor-publish"
              >
                Review &amp; publish
              </Button>
            )}
          </StickyActionBar>
        )}
      </form>

      <ReviewPublishDialog
        open={reviewOpen}
        policy={policy}
        publishing={publishPolicy.isPending}
        refusal={publishRefusal}
        onClose={() => setReviewOpen(false)}
        // Each dialog closes only on success; a refusal leaves it open with the server's reason.
        onConfirm={() => publishPolicy.mutate(undefined, { onSuccess: () => setReviewOpen(false) })}
      />
      <SavePublishedDialog
        open={confirmSaveOpen}
        saving={saveSections.isPending}
        refusal={refusal}
        onClose={() => setConfirmSaveOpen(false)}
        // The sticky bar carries a refusal too, but sits behind this dialog's backdrop, so the
        // dialog has to stay open to show it.
        onConfirm={() =>
          saveSections.mutate(sectionsBody(formik.values), {
            onSuccess: () => setConfirmSaveOpen(false),
          })
        }
      />
      <DiscardDraftDialog
        open={discardOpen}
        discarding={discardPolicy.isPending}
        refusal={discardRefusal}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() =>
          discardPolicy.mutate(undefined, {
            onSuccess: () => {
              setDiscardOpen(false)
              onBack()
            },
          })
        }
      />
    </Box>
  )
}

/** The four-section editor for one policy configuration. */
const BenefitPolicyEditor = ({ policyId, onBack }: BenefitPolicyEditorProps) => {
  const { data: policy, isPending: isLoading, isError } = usePolicyConfiguration(policyId)

  if (isError) {
    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Alert severity="error" data-testid="editor-error">
          Could not load this policy. Refresh to try again.
        </Alert>
      </Box>
    )
  }
  if (isLoading || !policy) return <Loader />

  // Remounting per policy keeps the form from carrying one configuration's edits into another.
  return <EditorForm key={policy.id} policy={policy} onBack={onBack} />
}

export default BenefitPolicyEditor
